import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TransformationPanel } from '../components/jobs/TransformationPanel';
import { useBulkSelection } from '../contexts/BulkSelectionContext';
import { productService } from '../services/product';
import { toast } from '../hooks/use-toast';

export const CreateJobsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selection, clearSelection } = useBulkSelection();
  
  const [storeId, setStoreId] = useState<string>('');
  const [loadingStoreId, setLoadingStoreId] = useState(false);
  
  // Get store_id from URL or bulk selection
  useEffect(() => {
    const urlStoreId = searchParams.get('store_id');
    if (urlStoreId) {
      setStoreId(urlStoreId);
    } else if (selection.selectedIds.size > 0) {
      // Get store ID from first selected product
      setLoadingStoreId(true);
      const firstProductId = Array.from(selection.selectedIds)[0];
      productService.getProduct(firstProductId)
        .then(product => {
          if (product && product.store_id) {
            setStoreId(product.store_id);
          }
        })
        .catch(error => {
          console.error('Failed to get product store ID:', error);
          toast({
            title: 'Error',
            description: 'Failed to determine store for selected products',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setLoadingStoreId(false);
        });
    }
  }, [searchParams, selection.selectedIds]);

  const handleJobsCreated = (identifier: string, isAsync?: boolean, metadata?: any) => {
    if (isAsync) {
      // Navigate to async job monitor
      const params = new URLSearchParams({
        job_id: identifier,
        ...(metadata?.storeId && { store_id: metadata.storeId }),
        ...(metadata?.storeName && { store_name: metadata.storeName }),
        ...(metadata?.productCount && { product_count: metadata.productCount.toString() })
      });
      navigate(`/jobs/async-monitor?${params}`);
    } else {
      // Navigate to regular job monitor
      navigate(`/jobs/monitor?batch_id=${identifier}`);
    }
    
    // Clear bulk selection after creating jobs
    clearSelection();
  };

  const handleCancel = () => {
    navigate('/jobs');
  };

  if (!storeId && !loadingStoreId) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No store ID or selected products found</p>
        <button 
          onClick={() => navigate('/products')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded"
        >
          Select Products
        </button>
      </div>
    );
  }

  if (loadingStoreId) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-gray-500">Loading store information...</p>
      </div>
    );
  }

  const selectedProductIds = selection.selectedIds.size > 0 
    ? Array.from(selection.selectedIds) 
    : searchParams.get('product_ids')?.split(',') || [];

  const imageCount = 0; // TODO: Calculate from selected products

  return (
    <TransformationPanel
      selectedProductIds={selectedProductIds}
      storeId={storeId}
      imageCount={imageCount}
      onJobsCreated={handleJobsCreated}
      onCancel={handleCancel}
    />
  );
};

export default CreateJobsPage;